import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  coverUrl,
  EMPTY_READING_LIBRARY,
  libraryTypes,
  loadLibraryPassage,
  loadReadingLibrary,
  READING_LIBRARY_HANDLE,
} from '../content/reading-library';
import { usedLineages } from '../content/sessions';
import { inDesignOrder, LIBRARY_CATEGORY_LABEL, type LibraryEntry } from '../domain/library';
import { queryKeys } from '../query/keys';
import { useConfig, useSession } from './provider';
import { useLocalSessions } from './use-local-sessions';
import { useWorkbook } from './use-workbook';

const DAY = 24 * 60 * 60_000;

/**
 * The Library tab's Reading shelf: the read-aloud library from Core, in its own order, with
 * its categories (only those that hold a book) and each book's covers. Empty where the
 * environment has no library. `ready` like `useNextMaterial`: until Core has answered, an
 * empty shelf means "not yet", not "nothing". (The Math shelf is the design's, not connected
 * yet: `domain/library-preview`.)
 */
export function useLibrary() {
  const session = useSession();
  const { strongerMemoryTypes, strongerMemoryGroupActorUuid, onearlyApiUrl } = useConfig();
  const types = libraryTypes(strongerMemoryTypes);
  const { i18n } = useLingui();
  const sessions = useLocalSessions();
  const library = useQuery({
    queryKey: queryKeys.library(READING_LIBRARY_HANDLE),
    enabled: session.status === 'authenticated' && types !== undefined,
    staleTime: DAY,
    queryFn: () =>
      loadReadingLibrary(types as NonNullable<typeof types>, strongerMemoryGroupActorUuid),
  });
  const ready = types === undefined || library.data !== undefined || library.isError;

  // A category's name in the person's language; the library's English one for a category the
  // app doesn't know yet.
  const categoryLabel = useMemo(() => {
    const named = new Map(
      (library.data ?? EMPTY_READING_LIBRARY).categories.map((category) => [
        category.key,
        category.label,
      ]),
    );
    return (key: string) => {
      const known = LIBRARY_CATEGORY_LABEL[key];
      return known ? i18n._(known) : named.get(key);
    };
  }, [library.data, i18n]);

  const reading = useMemo((): LibraryEntry[] => {
    const read = usedLineages(sessions.data ?? [], 'reading');
    return (library.data ?? EMPTY_READING_LIBRARY).books.map((book) => {
      // The tag is the book's category (its first, the one it's best known by).
      const first = book.categories[0];
      const tag = first ? categoryLabel(first) : undefined;
      const { card, row, background } = book.cover ?? {};
      return {
        lineageUuid: book.lineageUuid,
        uuid: book.uuid,
        title: book.title,
        ...(book.summary ? { summary: book.summary } : {}),
        used: read.has(book.lineageUuid),
        categories: book.categories,
        ...(book.minutes ? { minutes: book.minutes } : {}),
        ...(book.author ? { byline: book.author } : {}),
        ...(tag ? { tag } : {}),
        cover: {
          ...(card ? { card: coverUrl(onearlyApiUrl, card) } : {}),
          ...(row ? { row: coverUrl(onearlyApiUrl, row) } : {}),
          ...(background ? { background } : {}),
        },
      };
    });
  }, [library.data, sessions.data, categoryLabel, onearlyApiUrl]);

  const categories = useMemo(() => {
    const used = new Set(reading.flatMap((entry) => entry.categories ?? []));
    return inDesignOrder((library.data ?? EMPTY_READING_LIBRARY).categories)
      .filter((category) => used.has(category.key))
      .map((category) => ({
        key: category.key,
        label: categoryLabel(category.key) ?? category.label,
      }));
  }, [library.data, reading, categoryLabel]);

  /**
   * A row's tags (Figma library › row): the category, then how long it takes to read. In a
   * category's list the category is that one; anywhere else, the book's first.
   */
  const tagsFor = (entry: LibraryEntry, category?: string): string[] => {
    const key = category && entry.categories?.includes(category) ? category : entry.categories?.[0];
    const named = key ? categoryLabel(key) : undefined;
    const minutes = entry.minutes;
    return [...(named ? [named] : []), ...(minutes ? [i18n._(msg`${minutes} min`)] : [])];
  };

  return {
    reading,
    categories,
    tagsFor,
    ready,
    /** Core could not be read, and there's nothing cached to show instead. */
    failed: library.isError && library.data === undefined,
    retry: () => void library.refetch(),
  };
}

/** Whether this environment has the read-aloud library (the chooser's "Choose from Library"). */
export function useHasLibrary(): boolean {
  return libraryTypes(useConfig().strongerMemoryTypes) !== undefined;
}

/**
 * A passage chosen in the Library, with its text, for the reading exercise. `loading` while
 * Core answers; `passage` stays undefined for a lineage that isn't a library passage (the
 * workbook's own passages are read from the workbook).
 */
export function useLibraryPassage(lineageUuid: string | undefined) {
  const session = useSession();
  const { strongerMemoryTypes } = useConfig();
  const types = libraryTypes(strongerMemoryTypes);
  const enabled =
    session.status === 'authenticated' && types !== undefined && lineageUuid !== undefined;
  const query = useQuery({
    queryKey: queryKeys.libraryPassage(lineageUuid ?? ''),
    enabled,
    staleTime: DAY,
    queryFn: async () =>
      (await loadLibraryPassage(types as NonNullable<typeof types>, lineageUuid as string)) ?? null,
  });
  return { passage: query.data ?? undefined, loading: enabled && query.isPending };
}

/**
 * The passage a person chose in the Library, by lineage: the workbook's own passages first,
 * then the read-aloud library's (its text read from Core). `loading` until whichever holds it
 * has answered; `passage` undefined when neither does, so the exercise falls back to "pick
 * for me".
 */
export function useChosenPassage(lineageUuid: string | undefined) {
  const workbook = useWorkbook();
  const fromWorkbook = lineageUuid
    ? workbook.data?.passages.find((candidate) => candidate.lineageUuid === lineageUuid)
    : undefined;
  const workbookLoading = workbook.isPending && workbook.fetchStatus !== 'idle';
  const library = useLibraryPassage(
    lineageUuid !== undefined && !fromWorkbook && !workbookLoading ? lineageUuid : undefined,
  );
  const passage: { lineageUuid: string; title: string; body: string } | undefined =
    fromWorkbook ?? library.passage;
  return {
    passage,
    loading: lineageUuid !== undefined && !fromWorkbook && (workbookLoading || library.loading),
  };
}
